// Ficha del producto (decisión D49): "‹ Volver", la foto grande, el nombre, la presentación, el precio y "Agregar" con
// − cantidad +. Se abre al instante con lo que trajo la tarjeta y se completa con la API (con sesión, con el precio del
// cliente). En computadora, la foto a la izquierda (no más de 26rem) y los datos a la derecha.
import { Icono } from '@reparto/ui';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { ErrorCarga } from '../componentes/base';
import { ControlCantidad } from '../componentes/Cantidad';
import { EsqueletoFicha } from '../componentes/Esqueletos';
import { Pantalla } from '../componentes/Marco';
import { iconoDeProducto } from '../componentes/Miniatura';
import { EstadoStock, topeDe } from '../componentes/Producto';
import { FotoProducto, PrecioGrande } from '../componentes/TarjetaProducto';
import { bultosDe, fijarBultos } from '../datos/carrito';
import { useConsulta } from '../datos/consultas';
import { cambiarCarrito, useCarrito } from '../datos/estado-carrito';
import { cantidadEnUnidad, presentacion } from '../datos/formato';
import { claveFuente, traerArticulo, useFuente, useReglas } from '../datos/fuente';
import { useMarca } from '../datos/marca';
import { useSesion } from '../datos/sesion';
import type { Articulo } from '../datos/tipos';
import { rutaCategoria } from './Inicio';

export function Producto() {
  const { id = '' } = useParams();
  const { state } = useLocation() as { state: { articulo?: Articulo } | null };
  const navegar = useNavigate();
  const fuente = useFuente();
  const reglas = useReglas();
  const conSesion = useSesion((s) => !!s.sesion);
  const sinPrecios = useMarca((s) => s.marca?.catalogo?.publico === 'sin_precios');
  const consulta = useConsulta(fuente ? `articulo:${claveFuente(fuente)}:${id}` : null, () =>
    traerArticulo(fuente!, id),
  );
  // Lo que trajo la tarjeta sirve mientras llega la ficha (con sesión el precio puede ser otro: se espera la de la API).
  const a = consulta.datos ?? (state?.articulo?.id === id && !conSesion ? state.articulo : undefined);
  const bultos = useCarrito((s) => bultosDe(s.carrito, id));
  const volver = () => (window.history.length > 1 ? navegar(-1) : navegar('/'));

  return (
    <Pantalla amplia>
      <div className="px-4 pt-4 pb-8">
        <button
          type="button"
          onClick={volver}
          className="-ml-2 inline-flex min-h-11 items-center gap-1 rounded-full px-2 text-[15px] font-medium text-stone-600 hover:bg-stone-200/60"
        >
          <Icono nombre="chevron_left" tamano={22} />
          Volver
        </button>
        {!a ? (
          consulta.error ? (
            <div className="mt-4">
              <ErrorCarga
                mensaje={consulta.error}
                sinRed={consulta.sinRed}
                onReintentar={() => void consulta.recargar()}
              />
            </div>
          ) : (
            <EsqueletoFicha />
          )
        ) : (
          <article className="mt-3 md:grid md:grid-cols-[minmax(0,26rem)_minmax(0,1fr)] md:items-start md:gap-10">
            <div className="rounded-2xl bg-white p-2 ring-1 ring-stone-200">
              <FotoProducto a={a} grande className="max-h-[30dvh] md:max-h-none" />
            </div>
            <div className="mt-5 md:mt-0">
              {a.categoria ? (
                <Link
                  to={rutaCategoria(a.categoria)}
                  className="inline-flex min-h-9 items-center gap-1.5 rounded-full bg-primary-50 px-3 text-sm font-semibold text-primary-800 hover:bg-primary-100"
                >
                  <Icono nombre={iconoDeProducto(a.categoria)} tamano={18} />
                  {a.categoria}
                </Link>
              ) : null}
              <h1 className="mt-3 text-[26px] leading-tight font-bold text-stone-900 md:text-[32px]">{a.nombre}</h1>
              <p className="mt-1 text-[17px] text-stone-600">
                {presentacion(a.unidad, a.unidades_por_bulto)}
                {a.codigo ? <span className="text-stone-600"> · Código {a.codigo}</span> : null}
              </p>
              <div className="mt-4">
                {a.precio_bulto !== null ? (
                  <PrecioGrande precio={a.precio_bulto} unidad={a.unidad} ficha />
                ) : sinPrecios && !conSesion ? (
                  <Link to="/entrar" className="text-lg font-semibold text-primary-700 underline">
                    Entrá para ver el precio
                  </Link>
                ) : null}
              </div>
              <div className="mt-2">
                <EstadoStock a={a} />
              </div>
              <div className="mt-5 max-w-sm">
                <ControlCantidad
                  destacado
                  valor={bultos}
                  onCambiar={(n) => cambiarCarrito((c) => fijarBultos(c, a, n))}
                  max={topeDe(a, reglas.permitirSinStock)}
                  nombre={a.nombre}
                  deshabilitado={!a.se_puede_pedir && bultos === 0}
                  textoAgregar={a.se_puede_pedir ? 'Agregar al carrito' : 'Sin stock'}
                />
              </div>
              {bultos > 0 ? (
                <p className="mt-3 flex items-center gap-2 text-[15px] text-stone-700">
                  <Icono nombre="check_circle" tamano={20} className="text-primary-700" />
                  Tenés {cantidadEnUnidad(bultos, a.unidad)} en el carrito.
                  <Link to="/carrito" className="font-semibold text-primary-700 underline">
                    Ver carrito
                  </Link>
                </p>
              ) : null}
            </div>
          </article>
        )}
      </div>
    </Pantalla>
  );
}
